import { useMemo, useCallback } from 'react';
import { CONSTANTS } from '../data.js';
import { combinePair, eraIndexByKey, recipesByResult } from '../lib/utils';
import type { Instance, Effect } from '../types';
import { usePlayerProgress } from './usePlayerProgress';
import { useBoardState } from './useBoardState';
import { useGameEvents } from './useGameEvents';

export const useGameState = () => {
  const {
    discovered, setDiscovered,
    unlockedEras, setUnlockedEras,
    completedCollections, setCompletedCollections,
    completedTutorials, setCompletedTutorials,
    playerAvatar, setPlayerAvatar,
    playerDeck, setPlayerDeck,
    currentEraKey, currentEraName,
    resetPlayerProgress,
  } = usePlayerProgress();

  const {
    instances, setInstances,
    effects, setEffects,
    addInstance, removeInstanceById, updateInstancePos, resetBoard,
    idCounter,
  } = useBoardState();

  const {
    newDiscovery, setNewDiscovery,
    newEraUnlock, setNewEraUnlock,
    newCollectionUnlock, setNewCollectionUnlock,
    eraToAdvanceTo, setEraToAdvanceTo,
    unsuccessfulCombos, setUnsuccessfulCombos,
    hint, setHint,
    tutorialState, setTutorialState,
    resetGameEvents,
  } = useGameEvents();

  const totalElements = useMemo(() => Object.keys(CONSTANTS.elements).length, []);

  const elementsByEra = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const era of CONSTANTS.eras.list) {
      map[era.key] = [];
    }
    for (const key of Object.keys(CONSTANTS.elements)) {
      const e = CONSTANTS.elements[key];
      if (!map[e.era]) map[e.era] = [];
      map[e.era].push(key);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const A = CONSTANTS.elements[a].name.toLowerCase();
        const B = CONSTANTS.elements[b].name.toLowerCase();
        return A.localeCompare(B);
      });
    }
    return map;
  }, []);

  const startTutorial = useCallback((key: string) => {
    if (!completedTutorials.includes(key)) {
      setTutorialState({ active: key, step: 0 });
    }
  }, [completedTutorials, setTutorialState]);

  const advanceTutorial = useCallback(() => {
    setTutorialState(prev => {
      if (!prev.active) return prev;
      const tutorial = CONSTANTS.tutorials[prev.active];
      if (!tutorial || prev.step >= tutorial.steps.length - 1) {
        return prev;
      }
      return { ...prev, step: prev.step + 1 };
    });
  }, [setTutorialState]);

  const completeTutorial = useCallback(() => {
    if (tutorialState.active && !completedTutorials.includes(tutorialState.active)) {
      setCompletedTutorials(prev => [...prev, tutorialState.active!]);
    }
    setTutorialState({ active: null, step: 0 });
  }, [tutorialState.active, completedTutorials, setCompletedTutorials, setTutorialState]);

  const advanceToEra = useCallback((eraKey: string) => {
    if (!unlockedEras.includes(eraKey)) {
        const newEras = [...unlockedEras, eraKey];
        setUnlockedEras(newEras);
        
        const era = CONSTANTS.eras.list.find(e => e.key === eraKey);
        if (era) {
            setNewEraUnlock({ key: era.key, name: era.name, description: era.description });
        }
    }
    setEraToAdvanceTo(null);
  }, [unlockedEras, setUnlockedEras, setNewEraUnlock, setEraToAdvanceTo]);

  const completeCombination = useCallback((effect: Effect) => {
    addInstance(effect.key, effect.x, effect.y);
    if (!discovered.includes(effect.key)) {
      setUnsuccessfulCombos(0);
      const el = CONSTANTS.elements[effect.key];
      const newDiscoveredList = [...discovered, effect.key];
      setDiscovered(newDiscoveredList);
      setNewDiscovery({ key: effect.key, label: el.name, description: el.description });

      if (tutorialState.active === 'first_combination' && tutorialState.step === 2 && effect.key === 'earth') {
        advanceTutorial();
      }

      // Check for collection completion
      const newlyCompleted = CONSTANTS.collections.find(c => 
        !completedCollections.includes(c.key) && c.elements.every(elKey => newDiscoveredList.includes(elKey))
      );

      if (newlyCompleted) {
        setCompletedCollections(prev => [...prev, newlyCompleted.key]);
        setNewCollectionUnlock({
          key: newlyCompleted.key,
          name: newlyCompleted.name,
          lore: newlyCompleted.reward.lore,
          avatarKey: newlyCompleted.reward.avatarKey,
        });
      }

      let newUnlockedEras = [...unlockedEras];
      const isFirstDiscovery = newDiscoveredList.length === CONSTANTS.startingElements.length + 1;

      if (isFirstDiscovery && !newUnlockedEras.includes('stone_age')) {
        newUnlockedEras.push('stone_age');
        setUnlockedEras(newUnlockedEras);
        const stoneAgeEra = CONSTANTS.eras.list.find(e => e.key === 'stone_age');
        if (stoneAgeEra) {
          setNewEraUnlock({ key: stoneAgeEra.key, name: stoneAgeEra.name, description: stoneAgeEra.description });
        }
      }

      for (const unlockedEraKey of newUnlockedEras.sort((a, b) => eraIndexByKey[a] - eraIndexByKey[b])) {
        const unlockedEraIndex = eraIndexByKey[unlockedEraKey];
        if (unlockedEraIndex >= CONSTANTS.eras.list.length - 1) continue;

        const nextEra = CONSTANTS.eras.list[unlockedEraIndex + 1];
        if (newUnlockedEras.includes(nextEra.key) || (eraToAdvanceTo && eraToAdvanceTo.key === nextEra.key)) continue;

        const elementsInThisEra = elementsByEra[unlockedEraKey] || [];
        if (elementsInThisEra.length === 0) continue;

        const discoveredInThisEra = elementsInThisEra.filter(k => newDiscoveredList.includes(k));
        const threshold = Math.ceil(elementsInThisEra.length * CONSTANTS.eras.unlockThreshold);

        if (discoveredInThisEra.length >= threshold) {
          const prevEraName = CONSTANTS.eras.list[unlockedEraIndex].name;
          setEraToAdvanceTo({
            key: nextEra.key,
            name: nextEra.name,
            description: `You have mastered the secrets of the ${prevEraName}. The path to the ${nextEra.name} is now open!`,
          });
          break;
        }
      }
    }
    setEffects(prev => prev.filter(e => e.id !== effect.id));
  }, [addInstance, discovered, unlockedEras, elementsByEra, eraToAdvanceTo, completedCollections, setDiscovered, setNewDiscovery, setCompletedCollections, setNewCollectionUnlock, setUnlockedEras, setNewEraUnlock, setEraToAdvanceTo, setEffects, setUnsuccessfulCombos, tutorialState, advanceTutorial]);

  const tryCombine = useCallback((a: Instance, boardRect: DOMRect): string | 'fail' | null => {
    const threshold = CONSTANTS.drag?.threshold || 48;
    let target: Instance | null = null;
    let bestDist = Infinity;
    for (const b of instances) {
      if (b.id === a.id) continue;
      const dx = (a.x + 32) - (b.x + 32);
      const dy = (a.y + 32) - (b.y + 32);
      const d = Math.hypot(dx, dy);
      if (d < threshold && d < bestDist) {
        bestDist = d;
        target = b;
      }
    }

    if (!target) return null;

    const result = combinePair(a.elementKey, target.elementKey);

    if (result) {
      const rx = (a.x + target.x) / 2;
      const ry = (a.y + target.y) / 2;
      setInstances(prev => prev.filter(i => i.id !== a.id && i.id !== target!.id));
      setEffects(prev => [...prev, { id: idCounter.current++, x: rx, y: ry, key: result }]);
      return result;
    } else {
      setUnsuccessfulCombos(c => c + 1);
      const repulsionDist = CONSTANTS.drag?.repulsionDistance || 24;
      let dx = a.x - target.x;
      let dy = a.y - target.y;
      let dist = Math.hypot(dx, dy);

      if (dist < 1) {
        dx = 1; dy = 0; dist = 1;
      }

      const nx = dx / dist;
      const ny = dy / dist;
      const newX = a.x + nx * repulsionDist;
      const newY = a.y + ny * repulsionDist;
      
      const clampedX = Math.min(Math.max(newX, 8), boardRect.width - 72);
      const clampedY = Math.min(Math.max(newY, 64), boardRect.height - 72);

      updateInstancePos(a.id, clampedX, clampedY);
      return 'fail';
    }
  }, [instances, updateInstancePos, setInstances, setEffects, idCounter, setUnsuccessfulCombos]);

  const resetProgress = useCallback(() => {
    resetPlayerProgress();
    resetBoard();
    resetGameEvents();
  }, [resetPlayerProgress, resetBoard, resetGameEvents]);

  const getHint = useCallback(() => {
    const elementsInCurrentEra = elementsByEra[currentEraKey] || [];
    const undiscoveredInEra = elementsInCurrentEra.filter(key => !discovered.includes(key));
  
    const shuffledUndiscovered = [...undiscoveredInEra].sort(() => Math.random() - 0.5);
  
    for (const undiscoveredElement of shuffledUndiscovered) {
      const recipes = recipesByResult[undiscoveredElement] || [];
      const shuffledRecipes = [...recipes].sort(() => Math.random() - 0.5);
  
      for (const recipe of shuffledRecipes) {
        if (discovered.includes(recipe.a) && discovered.includes(recipe.b)) {
          setHint({
            result: undiscoveredElement,
            a: recipe.a,
            b: recipe.b,
          });
          setUnsuccessfulCombos(0);
          return;
        }
      }
    }
  }, [elementsByEra, currentEraKey, discovered, setHint, setUnsuccessfulCombos]);

  return {
    // State
    discovered,
    unlockedEras,
    instances,
    effects,
    newDiscovery,
    newEraUnlock,
    eraToAdvanceTo,
    completedCollections,
    completedTutorials,
    playerAvatar,
    playerDeck,
    newCollectionUnlock,
    unsuccessfulCombos,
    hint,
    tutorialState,
    
    // Derived State
    totalElements,
    currentEraKey,
    currentEraName,
    elementsByEra,

    // Setters & Logic
    setNewDiscovery,
    setNewEraUnlock,
    setNewCollectionUnlock,
    setPlayerAvatar,
    setPlayerDeck,
    setEraToAdvanceTo,
    setHint,
    addInstance,
    removeInstanceById,
    updateInstancePos,
    tryCombine,
    completeCombination,
    resetBoard,
    resetProgress,
    setInstances,
    setEffects,
    advanceToEra,
    getHint,
    startTutorial,
    advanceTutorial,
    completeTutorial,
  };
};
